import type * as React from "react"
import { useEffect, useState } from "react"
import { Container, type ContainerWidth } from "../layout/Layout.js"
import "./Topbar.css"

export interface TopbarProps {
  /** Left slot — wordmark, page title, breadcrumb. */
  readonly brand?: React.ReactNode
  /** Right slot — actions, typically ending in a ThemeToggle. */
  readonly actions?: React.ReactNode
  /** Hide the bar while scrolling down, reveal it on the way back up. @default false */
  readonly hideOnScroll?: boolean
  /**
   * The element whose scrolling drives `hideOnScroll`. Omit it and the window
   * is watched, which is right for a bar over a page that scrolls itself.
   *
   * Pass the scroll container when the bar is INSIDE one — a SidebarShell content
   * column, a panel, a bounded region. Watching the window there hides the bar
   * whenever the surrounding page moves, which from the reader's seat is the bar
   * vanishing while the text under it stands still.
   */
  readonly scrollTargetRef?: React.RefObject<HTMLElement | null>
  /** Centre slot — a search field or a segmented control. */
  readonly children?: React.ReactNode
  /**
   * Cap the bar's CONTENT at a `Container` measure so the brand's left edge lands on
   * the same vertical line as the page's own Container below it. Omitted, the bar
   * stays fluid with its own inline padding, which is right over a console or a
   * sidebar shell where nothing below it is a centred column.
   *
   * Implemented by rendering the three slots inside a real `Container` rather than
   * by re-typing its cap here, so the two can never drift.
   */
  readonly width?: ContainerWidth
}

/** Scroll movement below this is jitter (trackpad rubber-banding), not intent. */
const DELTA_FLOOR = 6
/** Inside this band the bar is always shown, so the top of a page is never bare. */
const TOP_BAND = 72

/**
 * Auto-hide state for a sticky bar. The scroll listener is passive and every
 * read is deferred into one `requestAnimationFrame` callback, so a fast scroll
 * measures the DOM once per frame instead of once per event — reading
 * `scrollY` inside the handler itself is the classic scroll-jank source.
 *
 * `targetRef` names the element whose scrolling counts. Omitted (or still empty)
 * means the window; a filled ref means that element's `scrollTop`, which is what
 * makes the bar usable inside a scrollable region instead of reacting to the page
 * behind it. The ref is READ INSIDE the effect rather than during render, because
 * a ref is only attached at commit time and a render-time read would be `null` on
 * the first pass.
 *
 * Returns `false` forever when the reader asked for reduced motion: a bar that
 * slides away is vestibular motion, and the honest reduction is to leave it
 * pinned rather than to hide it instantly. The preference is SUBSCRIBED to, not
 * sampled once at mount — a reader who turns "reduce motion" on mid-session used
 * to keep a sliding bar until the next remount. That is the JS half of the guard;
 * Topbar.css drops the transition as the CSS half, so neither layer alone has to
 * be trusted.
 */
function useHideOnScroll(
  enabled: boolean,
  targetRef: React.RefObject<HTMLElement | null> | undefined
): boolean {
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setHidden(false)
      return
    }

    // Read at effect time, never during render: a ref is only attached at commit.
    const target = targetRef?.current ?? null

    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)")
    // The scroll subscription is torn down and rebuilt when the preference
    // flips, so `reduce` is a live fact rather than a mount-time snapshot.
    let teardown: (() => void) | undefined

    const bind = () => {
      if (query?.matches === true) {
        setHidden(false)
        return
      }

      const scrollTop = () => (target === null ? window.scrollY : target.scrollTop)
      let last = scrollTop()
      let frame = 0

      const measure = () => {
        frame = 0
        const y = scrollTop()
        const delta = y - last
        if (Math.abs(delta) < DELTA_FLOOR) return
        last = y
        setHidden(y > TOP_BAND && delta > 0)
      }

      const onScroll = () => {
        if (frame !== 0) return
        frame = window.requestAnimationFrame(measure)
      }

      const source: Window | HTMLElement = target ?? window
      source.addEventListener("scroll", onScroll, { passive: true })
      teardown = () => {
        source.removeEventListener("scroll", onScroll)
        if (frame !== 0) window.cancelAnimationFrame(frame)
      }
    }

    bind()

    const onPreferenceChange = () => {
      teardown?.()
      teardown = undefined
      setHidden(false)
      bind()
    }
    query?.addEventListener?.("change", onPreferenceChange)

    return () => {
      query?.removeEventListener?.("change", onPreferenceChange)
      teardown?.()
    }
  }, [enabled, targetRef])

  return hidden
}

/**
 * The sticky bar over a content column: brand on the left, an optional centre
 * slot, actions on the right.
 *
 * A bare `<header>` and no name prop, deliberately. Inside SidebarShell this bar
 * sits in the content column beside `<main>`, where `<header>` resolves to a
 * generic element rather than the `banner` landmark — and a generic element
 * cannot carry an accessible name at all. Claiming `role="toolbar"` instead would
 * promise arrow-key navigation between the slots that this component does not
 * implement. The bar's contents name themselves (a wordmark, labelled buttons),
 * which is how this package's AppShell masthead also works.
 *
 * `hideOnScroll` trades a little chrome for reading room on long pages. The
 * hidden bar keeps its space in the sticky layer (it translates, it does not
 * unmount), so revealing it never reflows the article under it. It watches the
 * window by default; pass `scrollTargetRef` when the bar lives inside a
 * scrollable region, or the surrounding page's scrolling will hide it.
 */
export function Topbar({
  brand,
  actions,
  hideOnScroll = false,
  scrollTargetRef,
  width,
  children
}: TopbarProps) {
  const hidden = useHideOnScroll(hideOnScroll, scrollTargetRef)
  const slots = (
    <>
      {brand !== undefined && <div className="FfnTopbarBrand">{brand}</div>}
      {children !== undefined && <div className="FfnTopbarCenter">{children}</div>}
      {actions !== undefined && <div className="FfnTopbarActions">{actions}</div>}
    </>
  )
  return (
    <header
      className="FfnTopbar"
      data-slot="topbar"
      data-hidden={hidden}
      data-hide-on-scroll={hideOnScroll}
      data-width={width}
    >
      {width === undefined ? (
        slots
      ) : (
        <Container className="FfnTopbarInner" width={width}>
          {slots}
        </Container>
      )}
    </header>
  )
}
